export function validateEmail(email) {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
}

const cpfsInvalidos = [
  '00000000000',
  '11111111111',
  '22222222222',
  '33333333333',
  '44444444444',
  '55555555555',
  '66666666666',
  '77777777777',
  '88888888888',
  '99999999999',
];

export function validarCPF(cpf) {
  cpf = cpf.replace(/[^\d]+/g, '');

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  if (cpfsInvalidos.includes(cpf)) {
    return false;
  }

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i), 10) * (10 - i);
  }
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;

  if (parseInt(cpf.charAt(9), 10) !== digito1) {
    return false;
  }

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i), 10) * (11 - i);
  }
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;

  if (parseInt(cpf.charAt(10), 10) !== digito2) {
    return false;
  }

  return true;
}

const cnpjsInvalidos = [
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999',
];

export function validarCNPJ(cnpj) {
  cnpj = cnpj.replace(/[^\d]+/g, '');

  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }

  if (cnpjsInvalidos.includes(cnpj)) {
    return false;
  }

  const calcularDigito = (cnpj, pesos) => {
    const soma = cnpj
      .slice(0, pesos.length)
      .split('')
      .reduce((acc, digito, i) => acc + parseInt(digito, 10) * pesos[i], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const digito1 = calcularDigito(cnpj, pesos1);
  const digito2 = calcularDigito(cnpj + digito1, pesos2);

  return (
    parseInt(cnpj.charAt(12), 10) === digito1 &&
    parseInt(cnpj.charAt(13), 10) === digito2
  );
}

////////////////////////////////////

/**
 * Valida um código PIX "copia e cola" (EMV / BR Code)
 * Compatível com navegador (sem Buffer)
 * Retorna: { valid: boolean, errors: string[], parsed: object|null }
 */

export function validatePixCopiaCola(payload) {
  const errors = [];

  // --- 1) Limpeza ---
  if (typeof payload !== 'string' || payload.trim().length === 0) {
    return {
      valid: false,
      errors: ['Payload vazio ou não é string'],
      parsed: null,
    };
  }

  payload = payload
    .trim()
    .replace(/[^\x20-\x7E]/g, '') // remove caracteres não ASCII (BOM, etc)
    .replace(/\s+/g, ''); // remove espaços e quebras

  // --- 2) Tamanho máximo (512 bytes) ---
  const bytes = new TextEncoder().encode(payload).length;
  if (bytes > 512) {
    errors.push(`Tamanho excede 512 bytes (atual: ${bytes})`);
  }

  // --- 3) Parser TLV ---
  function parseTLV(str) {
    const map = {};
    let i = 0;
    while (i < str.length) {
      if (i + 4 > str.length) throw new Error(`TLV truncado na posição ${i}`);
      const tag = str.slice(i, i + 2);
      const len = parseInt(str.slice(i + 2, i + 4), 10);
      if (Number.isNaN(len))
        throw new Error(`Length inválido para tag ${tag} na posição ${i + 2}`);
      const valueStart = i + 4;
      const valueEnd = valueStart + len;
      if (valueEnd > str.length)
        throw new Error(
          `Valor da tag ${tag} ultrapassa final do payload (esperado ${valueEnd}, len ${str.length})`,
        );
      const value = str.slice(valueStart, valueEnd);
      map[tag] = value;
      i = valueEnd;
    }
    return map;
  }

  let parsed;
  try {
    parsed = parseTLV(payload);
  } catch (e) {
    errors.push(`Erro ao parsear TLV: ${e.message}`);
    return { valid: false, errors, parsed: null };
  }

  // --- 4) Validação do CRC (tag 63) ---
  const crcTag = parsed['63'];
  if (!crcTag) {
    errors.push('Tag CRC (63) ausente');
  } else {
    const idx63 = payload.indexOf('63');
    if (idx63 === -1) {
      errors.push('Não foi possível localizar posição da tag 63');
    } else {
      const payloadSemCRC = payload.slice(0, idx63);
      const computedCRC = crc16ccitt(payloadSemCRC).toUpperCase();
      if (crcTag.toUpperCase() !== computedCRC) {
        errors.push(
          `CRC inválido (esperado ${computedCRC}, encontrado ${crcTag})`,
        );
      }
    }
  }

  // --- 5) Outras validações opcionais ---
  if (parsed['00'] !== '01')
    errors.push('Tag 00 (Payload Format Indicator) inválida ou ausente');
  if (!parsed['26'] && !parsed['27'])
    errors.push('Tag 26/27 (merchant account info) ausente');

  return { valid: errors.length === 0, errors, parsed };
}

/**
 * Calcula CRC-16/CCITT (padrão EMV/PIX)
 * Polinômio 0x1021, init 0xFFFF
 */
function crc16ccitt(inputStr) {
  const encoder = new TextEncoder();
  const buf = encoder.encode(`${inputStr}6304`); // inclui "63" + "04" conforme padrão EMV
  let crc = 0xffff;
  for (const b of buf) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
      crc &= 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}
